import { PerformanceLog } from '@/types/app.types';

/**
 * Parses numeric values from tactical strings (e.g., "12-15" -> 15, "100kg" -> 100).
 */
export const extractHighNum = (val: string | number | null | undefined): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    const matches = val.match(/([0-9]+(\.[0-9]+)?)/g);
    if (!matches) return 0;
    return Math.max(...matches.map(Number));
};

/**
 * parseCardioMetrics: Safely transforms user input (Minutes, Kilometers, Intensity %)
 * into standard database metrics (Seconds, Meters, RPE).
 */
export const parseCardioMetrics = (
    minStr: string,
    kmStr: string,
    intStr: string,
    defaultDurationSecs: number = 0,
    defaultDistanceM: number = 0,
    defaultRpe: number = 0
): Partial<PerformanceLog> => {
    const min = parseFloat(minStr);
    const km = parseFloat(kmStr);
    const intensity = parseFloat(intStr);

    return {
        weight: '0', 
        reps: 1,     
        rpe: !isNaN(intensity) ? intensity : defaultRpe,
        duration_seconds: !isNaN(min) ? min * 60 : defaultDurationSecs,
        distance_meters: !isNaN(km) ? km * 1000 : defaultDistanceM
    };
};

export interface SessionMetrics {
    volume: number;
    sets: number;
    rpe: number;
    is_weighted: boolean;
}

/**
 * Calculates high-level metrics for an entire session's logs.
 */
export const calculatePerformanceMetrics = (logs: any[] = []): SessionMetrics => {
    if (!logs || logs.length === 0) return { volume: 0, sets: 0, rpe: 0, is_weighted: false };

    let totalVolume = 0;
    let totalSets = logs.length;
    let rpeSum = 0;
    let rpeCount = 0;

    logs.forEach(log => {
        const w = extractHighNum(log.weight) || 0;
        const r = parseInt(log.reps) || 0;
        const rpe = typeof log.rpe === 'string' ? parseFloat(log.rpe) : log.rpe;

        totalVolume += (w * r);
        if (typeof rpe === 'number' && !isNaN(rpe) && rpe > 0) {
            rpeSum += rpe;
            rpeCount++;
        }
    });

    return {
        volume: Math.round(totalVolume),
        sets: totalSets,
        rpe: rpeCount > 0 ? parseFloat((rpeSum / rpeCount).toFixed(1)) : 0,
        is_weighted: totalVolume > 0
    };
};

/**
 * getItemAccentColor: Contextual status coloring.
 */
export const getItemAccentColor = (isComplete: boolean, isActive: boolean, isSystemChoice: boolean, hasStarted: boolean): string => {
    if (isComplete) return '#2ecc71'; 
    if (isActive || isSystemChoice) return '#f29b11';   
    if (hasStarted) return '#2ecc71'; 
    return '#444'; 
};

/**
 * getItemAnimationClass: Pulsing logic for UI focus.
 */
export const getItemAnimationClass = (isComplete: boolean, isSystemChoice: boolean, isActive: boolean): string => {
    return (isSystemChoice && !isActive && !isComplete) ? 'animate-pulse' : '';
};
